import React, { useEffect, useState } from 'react';
import { ArrowLeft, ShieldAlert, Clock, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { getCaseByViewerToken } from '../services/supabase';
import type { CaseStatusView, ResolutionActionTag } from '../types';
// 表記は管理画面と共通（以前は管理画面が「解決済み」でずれていた）
import { statusLabel } from '../utils/caseLabels';
import { splitLessonByLanguage } from '../utils/caseClassification';

/* 報告した本人向けの教訓。

   非日本語の報告では教訓に「原言語版 →【日本語訳】→ 日本語版」が
   併記されている。この画面を見るのは**報告した本人**なので、
   自分の言語の版を先に出す。日本語版は畳んでおく（現場で日本語の
   掲示や指示と突き合わせたいことがあるため、消さない）。

   日本語の報告では併記が無く、native が null になるので
   従来どおり日本語だけが出る。 */
const ReporterLesson: React.FC<{ lesson: string }> = ({ lesson }) => {
  const { native, japanese } = splitLessonByLanguage(lesson);
  const [showJapanese, setShowJapanese] = useState(false);

  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
      <p className="text-xs text-emerald-700 font-medium mb-2">AIが抽出した教訓</p>
      <div className="text-sm text-emerald-900 leading-relaxed whitespace-pre-line">
        {native ?? japanese}
      </div>
      {native && (
        <div className="mt-3 pt-3 border-t border-emerald-200">
          <button
            type="button"
            onClick={() => setShowJapanese((v) => !v)}
            className="text-xs font-medium text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
          >
            {showJapanese ? '日本語版を隠す / Hide Japanese' : '日本語版を表示 / Show Japanese'}
          </button>
          {showJapanese && (
            <div className="mt-2 text-sm text-emerald-800 leading-relaxed whitespace-pre-line">
              {japanese}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// 実施アクションタグの表示ラベル（ManagerViews の RESOLUTION_CHECKLIST_OPTIONS と整合）
const resolutionTagLabel = (tag: ResolutionActionTag): string => {
  switch (tag) {
    case 'procedure_review': return '作業手順を見直した';
    case 'training': return '安全教育を実施した';
    case 'equipment_fix': return '設備に対策を施した';
    case 'documentation': return 'ルール・手順書を更新した';
    case 'communication': return '朝礼・掲示等で周知した';
    default: return tag;
  }
};

// ============================================================================
// MyReportView - 匿名投稿者が viewer_token を使って自分の報告を確認する画面
// ============================================================================
//
// URL: /my-report/{token}
// アクセス権: 誰でも（トークンを知っている人だけ）
// 表示: 状態 / リスクレベル / カテゴリ / 教訓 / 投稿時の本文
//
// 組織内構造（QR点名、チーム名、担当者名など）は意図的に出さない。
// RPC public.get_case_by_viewer_token がもともと返さない設計。

interface MyReportViewProps {
  token: string;
  onBack: () => void;
}

const statusBadgeClass: Record<CaseStatusView['status'], string> = {
  open: 'bg-amber-100 text-amber-800 border border-amber-200',
  in_progress: 'bg-blue-100 text-blue-800 border border-blue-200',
  resolved: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
};

const statusIcon: Record<CaseStatusView['status'], React.ReactNode> = {
  open: <Clock size={14} />,
  in_progress: <Loader2 size={14} className="animate-spin" />,
  resolved: <CheckCircle2 size={14} />,
};

const riskBadgeClass: Record<string, string> = {
  High: 'bg-red-100 text-red-800 border border-red-200',
  Medium: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
  Low: 'bg-green-100 text-green-800 border border-green-200',
};

const formatJst = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const MyReportView: React.FC<MyReportViewProps> = ({ token, onBack }) => {
  const [data, setData] = useState<CaseStatusView | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'notfound' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setState('loading');
      try {
        const result = await getCaseByViewerToken(token);
        if (cancelled) return;
        if (!result) {
          setState('notfound');
          return;
        }
        setData(result);
        setState('ok');
      } catch {
        if (!cancelled) setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-industrial-900 text-white">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1 text-sm text-slate-200 hover:text-white"
          >
            <ArrowLeft size={16} />
            戻る
          </button>
          <div className="flex-1 text-center flex items-center justify-center gap-2">
            <img src="/icon-512x512.png" alt="" className="h-5 w-5 rounded" />
            <span className="font-bold text-sm">安全ポスト＋</span>
          </div>
          <span className="w-12" aria-hidden />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="text-lg font-bold text-industrial-900 mb-1">あなたの報告の状況</h1>
        <p className="text-xs text-slate-500 mb-6">
          このページはトークンを知っている方だけが閲覧できます。匿名性は保たれています。
        </p>

        {state === 'loading' && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <Loader2 className="animate-spin mx-auto mb-3 text-slate-400" size={28} />
            <p className="text-sm text-slate-500">確認中...</p>
          </div>
        )}

        {state === 'notfound' && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <AlertCircle className="mx-auto mb-3 text-amber-500" size={28} />
            <p className="text-sm font-medium text-slate-700 mb-1">該当する報告が見つかりません</p>
            <p className="text-xs text-slate-500">
              URL が正しいかご確認ください。
              <br />
              失くしたトークンは復元できません。
            </p>
          </div>
        )}

        {state === 'error' && (
          <div className="bg-white border border-red-200 rounded-xl p-8 text-center">
            <AlertCircle className="mx-auto mb-3 text-red-500" size={28} />
            <p className="text-sm font-medium text-slate-700 mb-1">読み込みに失敗しました</p>
            <p className="text-xs text-slate-500">
              通信状況をご確認のうえ、しばらくしてから再度お試しください。
            </p>
          </div>
        )}

        {state === 'ok' && data && (
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <span
                  className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${statusBadgeClass[data.status]}`}
                >
                  {statusIcon[data.status]}
                  {statusLabel(data.status)}
                </span>
                <span
                  className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${riskBadgeClass[data.riskLevel] ?? 'bg-slate-100 text-slate-700'}`}
                >
                  リスク: {data.riskLevel}
                </span>
              </div>
              <h2 className="text-base font-bold text-industrial-900 mb-1">{data.title}</h2>
              <p className="text-xs text-slate-500">
                {formatJst(data.createdAt)} に報告 · 更新: {formatJst(data.updatedAt)}
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-500 font-medium mb-2">カテゴリ（4M分析）</p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 bg-slate-100 rounded text-slate-700">
                  {data.category1}
                </span>
                {data.category2 && (
                  <span className="px-2 py-1 bg-slate-100 rounded text-slate-700">
                    {data.category2}
                  </span>
                )}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-500 font-medium mb-2">あなたが報告した内容</p>
              <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                {data.body}
              </p>
            </div>

            {data.lesson && <ReporterLesson lesson={data.lesson} />}

            {data.imageUrl && (
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-xs text-slate-500 font-medium mb-2">添付画像</p>
                <img
                  src={data.imageUrl}
                  alt="報告画像"
                  className="w-full rounded-lg border border-slate-200"
                />
              </div>
            )}

            {/* B-1: 解決として記録された対応内容（migration 018 RPC が同梱して返す） */}
            {data.resolution && (
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 size={16} className="text-teal-700" />
                  <p className="text-xs text-teal-800 font-semibold">
                    現場での対応内容
                    <span className="ml-2 text-teal-600 font-normal">
                      {formatJst(data.resolution.resolvedAt)} 記録
                    </span>
                  </p>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-teal-700 font-medium mb-1">実施した対策</p>
                    <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                      {data.resolution.actionTaken}
                    </p>
                  </div>
                  {data.resolution.actionsChecklist.length > 0 && (
                    <div>
                      <p className="text-xs text-teal-700 font-medium mb-1">実施したアクション</p>
                      <ul className="text-sm text-slate-700 space-y-0.5">
                        {data.resolution.actionsChecklist.map(tag => (
                          <li key={tag}>・{resolutionTagLabel(tag)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-teal-700 font-medium mb-1">効果見込み</p>
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                      data.resolution.effectiveness === 'High' ? 'bg-emerald-100 text-emerald-800' :
                      data.resolution.effectiveness === 'Medium' ? 'bg-amber-100 text-amber-800' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {data.resolution.effectiveness === 'High' ? '高' : data.resolution.effectiveness === 'Medium' ? '中' : '低'}
                    </span>
                  </div>
                  {data.resolution.preventionNote && (
                    <div>
                      <p className="text-xs text-teal-700 font-medium mb-1">再発防止策</p>
                      <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                        {data.resolution.preventionNote}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-800">
              対応が進むと「対応中」「対応済み」に状態が変わります。
              <br />
              このページをブックマークしておくとあとで状況確認できます。
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
